import isFunction from 'lodash/isFunction';
import {RequestStatus} from '../enums/RequestStatus';

/** @type {ReduxRequestState} */
export const initialState = {
  status: RequestStatus.PENDING,
  err: null,
};

/**
 * @typedef {Object} ReduxRequestState
 * @property {RequestStatus} status
 * @property {?Error} err
 */

/**
 * @param {ReduxRequestActions} actions
 * @param {ReduxRequestOptions} options
 * @return {{initialState: ReduxRequestState, reducer: reducer}}
 */
export function buildReducer(actions, options) {
  const resetActions = (options.resetActions || []).map(action => action.TYPE);

  return {
    initialState,
    reducer,
    buildSubStateReducer,
  };

  /**
   * @param {ReduxRequestState} state
   * @param {ReduxAction<any>} action
   * @return {ReduxRequestState}
   */
  function reducer(state = initialState, action) {
    if (action.type === actions.resetRequest.TYPE || resetActions.includes(action.type)) {
      return {
        ...state,
        status: RequestStatus.PENDING,
        err: null,
      };
    }

    switch (action.type) {
      case actions.sendRequest.TYPE:
        return {
          ...state,
          status: RequestStatus.IS_FETCHING,
          err: null,
        };
      case actions.requestSuccess.TYPE:
        return {
          ...state,
          status: action.payload.status,
          err: null,
        };
      case actions.requestError.TYPE:
        const nextState = {
          ...state,
          status: RequestStatus.ERROR,
          err: action.payload.err,
        };
        if (isFunction(options.requestErrorReducer)) {
          return options.requestErrorReducer(nextState, action);
        } else {
          return nextState;
        }
      default:
        return state;
    }
  }

  /**
   * If the request is in a child state with other requests, this function provides an efficient wrapper to call the reducer without using combineReducers.
   * Note: The reducer does NOT use a default initialState so the reducer using this MUST set one or a null point exception will occur.
   * @param {String} property
   * @return {Function}
   */
  function buildSubStateReducer(property) {
    return function subStateReducer(parentState, action) {
      const state = parentState[property];
      switch (action.type) {
        case actions.resetRequest.TYPE:
        case actions.sendRequest.TYPE:
        case actions.requestSuccess.TYPE:
        case actions.requestError.TYPE:
          return {
            ...parentState,
            [property]: reducer(state, action),
          };
        default:
          return parentState;
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux-request/reducers.js